import { ButtonBase, type ButtonBaseProps } from "@mui/material";
import { PencilSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import { type PropsWithChildren, type ReactNode } from "react";
import { useIntl } from "react-intl";

import { IconTraveller, IconTravellerSelected } from "@/components/Icons";
import { type Traveller } from "@/types/types";

type Props = PropsWithChildren<
  Pick<ButtonBaseProps, "onClick"> & {
    traveller: Traveller | null;
    renderAdditionalServicesControls?: () => ReactNode;
  }
>;

export function TravellerSelectionButton({
  children,
  onClick,
  renderAdditionalServicesControls,
  traveller,
}: Props) {
  const intl = useIntl();
  const LeadingIcon = traveller ? IconTravellerSelected : IconTraveller;
  const TrailingIcon = traveller ? PencilSimpleIcon : PlusIcon;

  return (
    <div className="flex flex-col gap-2 rounded-3xl bg-white p-2">
      <ButtonBase
        onClick={onClick}
        sx={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "calc(var(--spacing) * 2)",
          padding: "calc(var(--spacing) * 2)",
          textAlign: "start",
          borderRadius: "var(--radius-3xl)",
        }}
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full border border-stroke-neutral-soft bg-surface-background">
          <LeadingIcon className="size-6" />
        </div>
        <span className="flex flex-col" data-sentry-mask>
          <span className="font-medium text-text-primary">
            {traveller
              ? [traveller.firstName, traveller.lastName].join(" ")
              : children}
          </span>
          <span className="text-sm font-normal text-text-tertiary">
            {traveller
              ? children
              : intl.formatMessage({
                  id: "travellers.addTravellerDetails",
                  defaultMessage: "Add traveller details",
                })}
          </span>
        </span>

        <TrailingIcon
          className="ms-auto shrink-0 text-surface-accent-strong"
          size={20}
          weight="bold"
        />
      </ButtonBase>
      {renderAdditionalServicesControls?.()}
    </div>
  );
}
